// SCR-STAFF-994 (جزئیات) — تیکت (US-CHN-042 تا 044؛ AP-CHN-02): خلاصه با SLA پاسخ اول و حل، رشته عمومی و یادداشت داخلی (AC-CHN-043.1)، پاسخ عمومی (اعلان پیامکی به
// مراجعه‌کننده) یا یادداشت داخلی، تخصیص، حل (پرسش رضایت و بستن خودکار ۷۲ ساعته — BR-CHN-042)، بازگشایی و لغو. شکایت بالینی با نشان «بالینی» و ثبت مشاهده
// (RecordView سمت سرور — BR-CHN-040).
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ChnTicketDetailView, type ChnTicketThreadMessage } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, ErrorState, faDigits, FormDialog, JalaliDate, KeyValueList, LoadingState, PageHeader, StatusChip } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { categoryLabel, chnTicketKeys, SCR_STAFF_994, slaTone, statusTone, ticketDescription, ticketSubject } from './ticketing';

export function TicketDetailPage() {
  const { t } = useTranslation('chn');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('chn.ticket.create');
  const key = chnTicketKeys.detail(session?.tenantId, id);
  const detail = useQuery({ queryKey: key, queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/tickets/{id}', { params: { path: { id } } })) });
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [awaitRequester, setAwaitRequester] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const apply = (next: ChnTicketDetailView) => {
    queryClient.setQueryData(key, next);
    void queryClient.invalidateQueries({ queryKey: chnTicketKeys.all(session?.tenantId), refetchType: 'none' });
  };
  const send = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/channels/tickets/{id}/messages', { params: { path: { id } }, body: { body: body.trim(), internal, awaitRequester: !internal && awaitRequester } })),
    onSuccess: (next) => {
      setBody('');
      setAwaitRequester(false);
      apply(next);
    },
  });
  const action = useMutation({
    mutationFn: (kind: 'resolve' | 'reopen' | 'cancel') =>
      unwrap(
        kind === 'resolve'
          ? tenantApi.POST('/api/v1/channels/tickets/{id}:resolve', { params: { path: { id } } })
          : kind === 'reopen'
            ? tenantApi.POST('/api/v1/channels/tickets/{id}:reopen', { params: { path: { id } } })
            : tenantApi.POST('/api/v1/channels/tickets/{id}:cancel', { params: { path: { id } } }),
      ),
    onSuccess: (next) => {
      setCancelling(false);
      apply(next);
    },
  });

  if (detail.isPending) {
    return (
      <Box data-screen-id={SCR_STAFF_994} component="section">
        <LoadingState rows={4} />
      </Box>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <Box data-screen-id={SCR_STAFF_994} component="section">
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      </Box>
    );
  }

  const data = detail.data;
  const ticket = data.ticket;
  const canHandle = data.canHandle && write.canWrite;
  const closed = ticket.status === 'Closed' || ticket.status === 'Cancelled';
  const resolved = ticket.status === 'Resolved';
  const platform = ticket.type === 'PlatformSupport';

  return (
    <Box data-screen-id={SCR_STAFF_994} component="section">
      <PageHeader
        titleKey="chn:tickets.detail.title"
        titleValues={{ number: faDigits(String(ticket.number)) }}
        badges={
          <Stack direction="row" spacing={1} alignItems="center">
            <span data-testid="chn-ticket-status" data-status={ticket.status}>
              <StatusChip tone={statusTone(ticket.status)} labelKey={`chn:tickets.status.${ticket.status}`} />
            </span>
            {ticket.slaState !== 'None' ? (
              <span data-testid="chn-ticket-sla" data-sla={ticket.slaState}>
                <StatusChip tone={slaTone(ticket.slaState)} labelKey={`chn:tickets.sla.${ticket.slaState}`} />
              </span>
            ) : null}
            {ticket.sensitivity === 'Clinical' ? <Chip size="small" color="error" label={t('chn:tickets.clinical')} data-testid="chn-ticket-clinical" /> : null}
          </Stack>
        }
        actions={
          canHandle ? (
            <>
              {!closed && !resolved ? (
                <Button onClick={() => setAssigning(true)} data-testid="chn-ticket-assign">
                  {t('chn:tickets.detail.assign')}
                </Button>
              ) : null}
              {!closed && !resolved && ticket.status !== 'New' ? (
                <Button variant="contained" onClick={() => action.mutate('resolve')} disabled={action.isPending} data-testid="chn-ticket-resolve">
                  {t('chn:tickets.detail.resolve')}
                </Button>
              ) : null}
              {resolved ? (
                <Button onClick={() => action.mutate('reopen')} disabled={action.isPending} data-testid="chn-ticket-reopen">
                  {t('chn:tickets.detail.reopen')}
                </Button>
              ) : null}
              {ticket.status === 'New' || ticket.status === 'Assigned' ? (
                <Button color="error" onClick={() => setCancelling(true)} data-testid="chn-ticket-cancel">
                  {t('chn:tickets.detail.cancel')}
                </Button>
              ) : null}
            </>
          ) : null
        }
      />
      <Stack spacing={2}>
        {action.error ? <ErrorState error={action.error} compact /> : null}
        {ticket.sensitivity === 'Clinical' ? (
          <Alert severity="warning" variant="outlined">
            {t('chn:tickets.detail.clinical_notice')}
          </Alert>
        ) : null}
        {platform ? <Alert severity="info">{t('chn:tickets.detail.platform_read_only')}</Alert> : null}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom data-testid="chn-ticket-subject-view">
              {ticketSubject(t, ticket.subject, ticket.source)}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
              {ticketDescription(t, data.description, ticket.source)}
            </Typography>
            <KeyValueList
              columns={3}
              items={[
                { labelKey: 'chn:tickets.columns.category', value: `${t(`chn:tickets.type.${ticket.type}`)} · ${categoryLabel(t, ticket.category, ticket.categoryName)}` },
                { labelKey: 'chn:tickets.columns.priority', value: t(`chn:tickets.priority.${ticket.priority}`) },
                { labelKey: 'chn:tickets.columns.source', value: t(`chn:tickets.source.${ticket.source}`) },
                {
                  labelKey: 'chn:tickets.columns.patient',
                  value: ticket.patientId ? (
                    <RouterLink to={`/patients/${ticket.patientId}`}>{ticket.patientName ?? t('chn:tickets.detail.patient_link')}</RouterLink>
                  ) : null,
                },
                { labelKey: 'chn:tickets.columns.assignee', value: ticket.assigneeName ?? (ticket.team ? t('chn:tickets.team', { team: t(`iam:role.${ticket.team}`, { defaultValue: ticket.team }) }) : null), testId: 'chn-ticket-assignee' },
                { labelKey: 'chn:tickets.detail.created_by', value: data.createdByName === '-' ? t(`chn:tickets.source.${ticket.source}`) : data.createdByName },
                { labelKey: 'chn:tickets.columns.created', value: <JalaliDate value={ticket.createdAt} withTime /> },
                { labelKey: 'chn:tickets.detail.first_response_due', value: ticket.firstResponseDueAt ? <JalaliDate value={ticket.firstResponseDueAt} withTime /> : null },
                { labelKey: 'chn:tickets.detail.first_responded', value: data.firstRespondedAt ? <JalaliDate value={data.firstRespondedAt} withTime /> : null },
                { labelKey: 'chn:tickets.detail.resolution_due', value: ticket.resolutionDueAt ? <JalaliDate value={ticket.resolutionDueAt} withTime /> : null },
                { labelKey: 'chn:tickets.detail.resolved_at', value: data.resolvedAt ? <JalaliDate value={data.resolvedAt} withTime /> : null },
                { labelKey: 'chn:tickets.detail.escalation', value: Number(ticket.escalationLevel) > 0 ? t('chn:tickets.detail.escalation_level', { level: faDigits(String(ticket.escalationLevel)) }) : null },
                { labelKey: 'chn:tickets.detail.rating', value: data.satisfactionRating ? faDigits(String(data.satisfactionRating)) : null, testId: 'chn-ticket-rating' },
                {
                  labelKey: 'chn:tickets.detail.closed_at',
                  value: data.closedAt ? (
                    <>
                      <JalaliDate value={data.closedAt} withTime />
                      {data.autoClosed ? ` — ${t('chn:tickets.detail.auto_closed')}` : ''}
                    </>
                  ) : null,
                },
              ]}
            />
          </CardContent>
        </Card>

        <Paper variant="outlined" component="section" aria-labelledby="chn-thread-title" sx={{ p: 2 }}>
          <Typography id="chn-thread-title" variant="h6" component="h2" gutterBottom>
            {t('chn:tickets.detail.thread')}
          </Typography>
          {data.messages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('chn:tickets.detail.empty_thread')}
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }} data-testid="chn-ticket-thread">
              {data.messages.map((message) => (
                <ThreadItem key={message.id} message={message} />
              ))}
            </Box>
          )}
          {!closed && !platform && (canHandle || write.canWrite) ? (
            <Stack spacing={1} sx={{ mt: 2 }} component="form" onSubmit={(event) => { event.preventDefault(); if (body.trim()) send.mutate(); }}>
              <TextField
                multiline
                minRows={3}
                label={t(internal ? 'chn:tickets.detail.compose_internal' : 'chn:tickets.detail.compose_public')}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'chn-ticket-compose' } }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                {data.canHandle ? (
                  <FormControlLabel control={<Checkbox checked={internal} onChange={(event) => setInternal(event.target.checked)} data-testid="chn-ticket-internal" />} label={t('chn:tickets.detail.internal_toggle')} />
                ) : null}
                {data.canHandle && !internal ? (
                  <FormControlLabel control={<Checkbox checked={awaitRequester} onChange={(event) => setAwaitRequester(event.target.checked)} />} label={t('chn:tickets.detail.await_requester')} />
                ) : null}
                <Box sx={{ flex: 1 }} />
                <Button type="submit" variant="contained" disabled={!body.trim() || send.isPending} data-testid="chn-ticket-send">
                  {t(internal ? 'chn:tickets.detail.send_internal' : 'chn:tickets.detail.send')}
                </Button>
              </Stack>
              {send.error ? <ErrorState error={send.error} compact /> : null}
            </Stack>
          ) : null}
        </Paper>
      </Stack>
      {assigning ? <AssignDialog ticketId={id} onClose={(next) => { setAssigning(false); if (next) apply(next); }} /> : null}
      {cancelling ? (
        <ConfirmDialog
          open
          titleKey="chn:tickets.detail.cancel_title"
          messageKey="chn:tickets.detail.cancel_message"
          confirmKey="chn:tickets.detail.cancel"
          destructive
          busy={action.isPending}
          error={action.error}
          onConfirm={() => action.mutate('cancel')}
          onCancel={() => setCancelling(false)}
        />
      ) : null}
    </Box>
  );
}

function ThreadItem({ message }: { message: ChnTicketThreadMessage }) {
  const { t } = useTranslation('chn');
  const system = message.author === 'System';
  return (
    <Box
      component="li"
      data-testid={`chn-ticket-message-${message.id}`}
      data-internal={message.internal ? 'true' : 'false'}
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: message.internal ? 'warning.main' : 'divider',
        bgcolor: message.internal ? 'action.hover' : 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
        <Typography variant="subtitle2" component="span">
          {system ? t('chn:tickets.detail.system') : `${message.authorName} · ${t(`chn:tickets.author.${message.author}`)}`}
        </Typography>
        {message.internal ? <Chip size="small" color="warning" variant="outlined" label={t('chn:tickets.detail.internal_note')} /> : null}
        <Typography variant="caption" color="text.secondary" component="span">
          <JalaliDate value={message.createdAt} withTime />
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {system ? t(`chn:tickets.detail.system_${message.body}`, { defaultValue: message.body }) : message.body}
      </Typography>
    </Box>
  );
}

function AssignDialog({ ticketId, onClose }: { ticketId: string; onClose: (next?: ChnTicketDetailView) => void }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const [membershipId, setMembershipId] = useState('');
  const assignees = useQuery({
    queryKey: ['chn', session?.tenantId, 'ticket-assignees'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/tickets/assignees')),
  });
  const assign = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/channels/tickets/{id}:assign', { params: { path: { id: ticketId } }, body: { membershipId } })),
    onSuccess: (next) => onClose(next),
  });
  return (
    <FormDialog
      open
      titleKey="chn:tickets.detail.assign"
      screenId={SCR_STAFF_994}
      onSubmit={() => membershipId && assign.mutate()}
      onClose={() => onClose()}
      isDirty={Boolean(membershipId)}
      isSubmitting={assign.isPending}
      submitError={assign.error ?? assignees.error}
      submitKey="chn:tickets.detail.assign_submit"
    >
      <TextField select required label={t('chn:tickets.detail.assignee')} value={membershipId} onChange={(event) => setMembershipId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'chn-ticket-assignee-select' } }}>
        {(assignees.data?.items ?? []).map((item) => (
          <MenuItem key={item.membershipId} value={item.membershipId}>
            {item.name}
            {item.roles.length > 0 ? ` — ${item.roles.map((role) => t(`iam:role.${role}`, { defaultValue: role })).join(t('chn:tickets.detail.role_separator'))}` : ''}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}
